import { useRef, useState } from 'react'

import { Box, Button, Text, ToggleButton } from '@factorialco/deprecated-design-system'
import { AttendanceShiftLocationTypeEnumValues } from 'generated/resources/datatable.graphql'
import {
  CreateAttendanceShiftMutationVariables,
  TimesheetPageEmployeesSettingFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import Select from 'modules/core/components/Select'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

import StartsNextDayCheckbox from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/ShiftRow/StartsNextDayCheckbox'
import TimeInput from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/ShiftRow/TimeInput'
import { hasDisabledSubmit } from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/ShiftRow/utils'
import { EVENT_SHIFT_ACTION } from 'modules/attendance/components/ClockIn/Store'
import TimeInconsistenciesAlert from 'modules/attendance/components/TimeTracking/Modals/RestTimeAlertsAndReminders/timeInconsistenciesAlert'
import { useShiftActions } from 'modules/attendance/hooks/shifts/useShiftActions'
import { useFetchBreakDurationRules } from 'modules/attendance/hooks/useFetchBreakDurationRules'
import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import { locationTypeText } from 'modules/attendance/lib/locationType'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import AttendanceFeatureUtils from 'modules/attendance/lib/utils/AttendanceFeatures'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import validateBreakDurationRules, {
  ValidationError,
  calculateOpenShiftDuration,
  isBreakSetting,
} from 'modules/attendance/lib/validateShiftBreakDurationRules'
import { LOCATION_TYPES } from 'modules/attendance/store/models/Shift'

interface Props {
  attendanceSetting: TimesheetPageEmployeesSettingFragment
  date: moment.Moment
  dayBreaks: AttendanceBreakConfigurations[]
  employeeId: number
  hasBreaksEnabled: boolean
  hasLocationEnabled: boolean
  lastShift: TimesheetPageShiftFragment | undefined
  showStartsNextDayCheckbox: boolean
}

const NewShiftForm = ({
  attendanceSetting,
  date,
  dayBreaks,
  employeeId,
  hasBreaksEnabled,
  hasLocationEnabled,
  lastShift,
  showStartsNextDayCheckbox,
}: Props) => {
  const { locationType } = lastShift || {}
  const lastShiftClockOutMoment = lastShift && AttendanceShiftUtils.clockOut(lastShift)
  const lastShiftClockOutTimeStr = lastShiftClockOutMoment
    ? dateToString(lastShiftClockOutMoment, 'ui_time')
    : null

  const defaultLocationType = hasLocationEnabled ? locationType || LOCATION_TYPES.OFFICE : null
  const [shouldShowInconsistencyAlert, setShouldShowInconsistencyAlert] = useState<boolean>(false)
  const [shouldShowShiftModal, setShouldShowShiftModal] = useState<boolean>(false)
  const { createShift } = useShiftActions()

  const attendanceSettingIds = attendanceSetting?.id ? [attendanceSetting.id] : []
  const { shifts, featureByName } = useTimesheetData()

  const hasPaidBreaksFeature = AttendanceFeatureUtils.hasPaidBreaks(featureByName)
  const currentShifts = AttendanceShiftUtils.currentShiftsForDate(moment(date), shifts)

  const initialShiftDate =
    showStartsNextDayCheckbox && lastShift?.showPlusOneDay
      ? date.clone().add(1, 'days')
      : date.clone()

  const defaultValues: CreateAttendanceShiftMutationVariables = {
    date: dateToString(initialShiftDate, 'backend'),
    referenceDate: dateToString(date, 'backend'),
    employeeId: employeeId,
    clockIn: lastShiftClockOutTimeStr ?? null,
    locationType: AttendanceShiftLocationTypeEnumValues.find(
      (location) => location === defaultLocationType
    ),
    timeSettingsBreakConfigurationId: hasPaidBreaksFeature
      ? dayBreaks[0]?.timeSettingsBreakConfigurationId
      : undefined,
    clockOut: null,
    workable: true,
    fetchDependencies: true,
  }

  const Separator = () => (
    <Box padding="s6">
      <Text.Normal color="grey600">-</Text.Normal>
    </Box>
  )

  const getSelectedBreak = (): AttendanceBreakConfigurations => {
    const selectedBreakId = form.values.timeSettingsBreakConfigurationId
    const breakName = dayBreaks.filter(
      (dayBreak: AttendanceBreakConfigurations) =>
        dayBreak.timeSettingsBreakConfigurationId === selectedBreakId
    )

    return {
      timeSettingsBreakConfigurationId: selectedBreakId,
      name: breakName[0]?.name ?? '',
      paid: breakName[0]?.paid,
    }
  }

  const form = useForm({
    defaultValues: defaultValues,
    onSubmit: async (data, { setErrors }) => {
      const clockIn = data.clockIn ? moment(`${data.date} ${data.clockIn}`).format() : null
      const clockOut = data.clockOut ? moment(`${data.date} ${data.clockOut}`).format() : null

      const response = await createShift({
        date: data.date,
        employeeId: data.employeeId,
        clockIn: clockIn,
        clockOut: clockOut,
        referenceDate: data.referenceDate ?? data.date,
        locationType: data.locationType,
        source: 'desktop',
        timeSettingsBreakConfigurationId: data.timeSettingsBreakConfigurationId,
        workable: data.workable,
        fetchDependencies: !AttendanceFeatureUtils.hasWorkedTimeMaterialized(featureByName),
      })
      const handled = handleResult(response, (errors) => {
        setErrors(errors)

        app.setFlash({
          type: 'wrong',
          message: errors?.base || errors?.messages || i18n.t('errors.generic_error'),
        })
      })

      if (handled.success && handled.data.shift) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        const isBreak = !data.workable
        const currentBreak = getSelectedBreak()
        const trackParams =
          hasPaidBreaksFeature && isBreak
            ? {
                break_type: currentBreak.paid,
                break_id: currentBreak.timeSettingsBreakConfigurationId,
              }
            : {}

        track(trackingEvents.timeTracking.editShiftManuallySubmit, {
          date: dateToString(date, 'backend'),
          action: EVENT_SHIFT_ACTION.ADD,
          location_type: data.locationType,
          wfh_enabled: hasLocationEnabled,
          type: isBreak ? 'break' : 'shift',
          ...trackParams,
        })

        if (!AttendanceFeatureUtils.hasWorkedTimeMaterialized(featureByName)) {
          // NOTE: When materialize view is enabled, we save some resources, we skip the dispatch event
          // because the worked time materialized is not ready yet. We trigger the event in useSubscriptionWorkedTimeRefreshed hook

          dispatch({
            type: EventTypes.ATTENDANCE_SHIFT_SAVED,
          })
        }

        const result = handled.data.shift

        form.resetForm({
          values: { ...defaultValues, clockIn: data.clockOut, date: result.date },
        })
        setShouldShowShiftModal(false)
        setShouldShowInconsistencyAlert(false)
      }
      return handled
    },
  })

  const closeModalAndReview = () => {
    setShouldShowShiftModal(true)
    setShouldShowInconsistencyAlert(false)
  }

  const { breakSettings, loading: loadingBreakSettings } =
    useFetchBreakDurationRules(attendanceSettingIds)

  const clockInString = form.values.clockIn
  const clockOutString = form.values.clockOut
  const shiftLengthInMinutes = useRef(0)

  useEffect(() => {
    if (clockInString && clockOutString) {
      shiftLengthInMinutes.current = calculateOpenShiftDuration(clockInString, clockOutString)
    }
  }, [clockInString, clockOutString])

  const [breakValdationErrors, setBreakValidationErrors] = useState<ValidationError[]>([])

  const checkForInconsistencies = () => {
    let errors: ValidationError[] = []

    if (
      breakSettings.length > 0 &&
      isBreakSetting(breakSettings[0]) &&
      breakSettings[0]?.restTime
    ) {
      const { values } = form
      const { date, referenceDate, employeeId, clockIn, clockOut, workable } = values
      const emptyShift = {
        id: 0,
        date,
        referenceDate: referenceDate || date,
        employeeId,
        clockIn,
        clockOut,
        locationType: AttendanceShiftLocationTypeEnumValues.find(
          (location) => location === locationType
        ),
        workable,
        crossesMidnight: false,
        isOvernight: false,
        minutes: 0,
        periodId: 0,
        showPlusOneDay: false,
      }
      errors = validateBreakDurationRules(currentShifts, breakSettings[0], emptyShift)
      setBreakValidationErrors(errors)

      const errorMessage = errors[errors.length - 1]
      const { minimalDuration = 0 } = errorMessage ?? {}
      const remainingMinutes = shiftLengthInMinutes.current - minimalDuration

      if (!(!errorMessage || remainingMinutes >= 0)) {
        setShouldShowShiftModal(false)
        setShouldShowInconsistencyAlert(true)
      } else {
        form.submitForm()
      }
    } else {
      form.submitForm()
    }
  }

  const checkHasDisabledSubmit = (): boolean => {
    return (
      form.isSubmitting ||
      loadingBreakSettings ||
      hasDisabledSubmit(date, '', form.values.clockIn, form.values.clockOut)
    )
  }

  const formWorkable = form.values.workable ?? true
  const showLocationSelect = formWorkable && hasLocationEnabled
  const showBreaksList = hasPaidBreaksFeature && !formWorkable

  const startsNextDay = form.values.date != form.values.referenceDate

  return (
    <>
      <Box alignItems="flexStart" width="full">
        <PopoverMenu
          type="dialog"
          open={shouldShowShiftModal}
          onClick={() => {
            form.resetForm({ values: defaultValues })
            setShouldShowShiftModal(!shouldShowShiftModal)
          }}
        >
          <Box paddingLeft="s32" maxHeight="s32">
            <PopoverMenu.Trigger>
              <Button.Decorative
                type="secondary"
                iconName="add"
                extra={{ 'data-intercom-target': 'attendance-row-add-shift-button' }}
              >
                {i18n.t('common.add')}
              </Button.Decorative>
            </PopoverMenu.Trigger>
          </Box>
          <PopoverMenu.Content side="right" align="start" width="full">
            <Box alignItems="flexStart" width="full" paddingX="s24" paddingY="s16" gap="s16">
              <Box flexDirection="row" gap="s8">
                <ToggleButton
                  toggled={formWorkable}
                  onClick={() => form.setFieldValue('workable', true)}
                  label={i18n.t('time_tracking.table.body.category.work')}
                />
                {hasBreaksEnabled && (
                  <ToggleButton
                    toggled={!formWorkable}
                    onClick={() => form.setFieldValue('workable', false)}
                    label={i18n.t('time_tracking.break')}
                  />
                )}
              </Box>
              <Box flexDirection="row" alignItems="center" height="s48">
                <TimeInput
                  readonly={false}
                  value={form.values.clockIn}
                  onChange={(value: string) => form.setFieldValue('clockIn', value)}
                  error={typeof form.errors.clockIn == 'string' ? form.errors.clockIn : undefined}
                />
                <Separator />
                <TimeInput
                  readonly={false}
                  value={form.values.clockOut}
                  onChange={(value: string) => form.setFieldValue('clockOut', value)}
                  error={typeof form.errors.clockOut == 'string' ? form.errors.clockOut : undefined}
                />
              </Box>
              {showStartsNextDayCheckbox && (
                <StartsNextDayCheckbox
                  checked={startsNextDay}
                  onClick={() => {
                    const newDate = !startsNextDay ? date.clone().add(1, 'days') : date

                    form.setValues({
                      ...form.values,
                      date: dateToString(newDate, 'backend'),
                    })
                  }}
                />
              )}
              {showLocationSelect && (
                <Select
                  label={i18n.t('clock_in.locations.modal.title')}
                  value={form.values.locationType ?? ''}
                  onChange={(value) =>
                    form.setValues({
                      ...form.values,
                      locationType: AttendanceShiftLocationTypeEnumValues.find(
                        (location) => location === value
                      ),
                    })
                  }
                  options={Object.values(LOCATION_TYPES).map((option) => ({
                    label: locationTypeText(option),
                    value: option,
                  }))}
                />
              )}
              {showBreaksList && (
                <Select
                  label={i18n.t('time_tracking.modals.locations.break_type')}
                  value={form.values.timeSettingsBreakConfigurationId?.toString() ?? ''}
                  onChange={(value) =>
                    form.setValues({
                      ...form.values,
                      timeSettingsBreakConfigurationId: +value,
                    })
                  }
                  options={dayBreaks.map((dayBreak: AttendanceBreakConfigurations) => ({
                    label: dayBreak.name,
                    value: dayBreak.timeSettingsBreakConfigurationId,
                  }))}
                />
              )}
            </Box>
            <Box
              flexDirection="row"
              justifyContent="flexEnd"
              gap="s8"
              paddingTop="s16"
              paddingRight="s24"
              border={{
                top: {
                  color: 'grey400',
                  width: 's1',
                  style: 'solid',
                },
              }}
            >
              <Button type="secondary" onClick={() => setShouldShowShiftModal(false)}>
                {i18n.t('common.cancel')}
              </Button>
              <Button disabled={checkHasDisabledSubmit()} onClick={() => checkForInconsistencies()}>
                {i18n.t('common.apply')}
              </Button>
            </Box>
          </PopoverMenu.Content>
        </PopoverMenu>
      </Box>
      {shouldShowInconsistencyAlert && (
        <TimeInconsistenciesAlert
          errors={breakValdationErrors}
          shiftLengthInMinutes={shiftLengthInMinutes.current}
          onClose={() => setShouldShowInconsistencyAlert(false)}
          onReview={closeModalAndReview}
          onSave={() => form.submitForm()}
        />
      )}
    </>
  )
}

export default observer(NewShiftForm)
